<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from '@ui/components/forms/button.vue';
import { readFileAsBase64 } from '@ui/composables/useBase64';
import { useHelp } from './useHelp';

const SCREENSHOT_FORMAT = 'image/jpeg';
const SCREENSHOT_QUALITY = 0.88;

const { helpData, startScreenshotCapture, endScreenshotCapture } = useHelp();
const texts = computed(() => helpData.value.texts);

interface CapturePayload {
    file: File;
    base64: string;
    width: number;
    height: number;
}

interface ErrorPayload {
    error: Error;
    message: string;
}

const props = withDefaults(
    defineProps<{
        disabled?: boolean;
    }>(),
    {
        disabled: false,
    },
);

const emit = defineEmits<{
    (event: 'capture', payload: CapturePayload): void;
    (event: 'error', payload: ErrorPayload): void;
}>();

const isCapturing = ref(false);
const localError = ref<string | null>(null);

const isSupported = computed(
    () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia),
);

const handleError = (error: Error) => {
    let message = texts.value.supportForm.fileUpload.errorDefault;

    switch (error.name) {
        case 'NotAllowedError':
            message = texts.value.supportForm.fileUpload.errorNotAllowed;
            break;
        case 'NotFoundError':
            message = texts.value.supportForm.fileUpload.errorNotFound;
            break;
        case 'AbortError':
            message = texts.value.supportForm.fileUpload.errorAbort;
            break;
        case 'InvalidStateError':
            message = texts.value.supportForm.fileUpload.errorInvalidState;
            break;
        default:
            message = `${texts.value.supportForm.fileUpload.errorDefault}: ${error.message}`;
    }

    localError.value = message || '';
    emit('error', { error, message: message || 'Screenshot capture failed.' });
};

const ensureStreamStopped = (stream: MediaStream | null) => {
    if (!stream) {
        return;
    }

    for (const track of stream.getTracks()) {
        if (track.readyState !== 'ended') {
            track.stop();
        }
    }
};

const waitForVideoFrame = async (video: HTMLVideoElement) => {
    await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => resolve();
    });
    await video.play();
    await new Promise((resolve) => setTimeout(resolve, 100));
};

const drawStreamToCanvas = async (stream: MediaStream) => {
    const [track] = stream.getVideoTracks();
    const context = document.createElement('canvas').getContext('2d');

    if (!context) {
        throw new Error(texts.value.supportForm.fileUpload.errorCanvas || 'Canvas is not available.');
    }

    const canvas = context.canvas;

    if (typeof ImageCapture !== 'undefined' && track) {
        try {
            const imageCapture = new ImageCapture(track);
            const bitmap = await imageCapture.grabFrame();
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            context.drawImage(bitmap, 0, 0);
            return canvas;
        } catch (imageCaptureError) {
            // Fall back to video element when ImageCapture is not available for the stream.
            console.warn('ImageCapture fallback activated', imageCaptureError);
        }
    }

    const video = document.createElement('video');
    video.srcObject = stream;
    await waitForVideoFrame(video);

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas;
};

const createScreenshotFile = (blob: Blob) => {
    const timestamp = Date.now();
    return new File([blob], `screenshot-${timestamp}.jpg`, { type: SCREENSHOT_FORMAT });
};

const createBlobFromCanvas = (canvas: HTMLCanvasElement) => {
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error(texts.value.supportForm.fileUpload.errorBlob || 'Failed to create image.'));
                }
            },
            SCREENSHOT_FORMAT,
            SCREENSHOT_QUALITY,
        );
    });
};

const handleClick = async () => {
    localError.value = null;

    if (!isSupported.value) {
        handleError(new Error(texts.value.supportForm.fileUpload.errorUnsupported || 'This browser does not support screenshot capture.'));
        return;
    }

    if (props.disabled || isCapturing.value) {
        return;
    }

    isCapturing.value = true;
    startScreenshotCapture();
    let stream: MediaStream | null = null;

    try {
        stream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                width: { ideal: 1920 },
                height: { ideal: 1080 },
            },
            audio: false,
        });

        const canvas = await drawStreamToCanvas(stream);
        const blob = await createBlobFromCanvas(canvas);
        const file = createScreenshotFile(blob);
        const base64 = await readFileAsBase64(file);

        emit('capture', {
            file,
            base64,
            width: canvas.width,
            height: canvas.height,
        });
    } catch (error) {
        if (error instanceof Error) {
            handleError(error);
        } else {
            handleError(new Error(texts.value.supportForm.fileUpload.errorUnexpected || 'An unexpected error occurred.'));
        }
    } finally {
        ensureStreamStopped(stream);
        isCapturing.value = false;
        endScreenshotCapture();
    }
};
</script>

<template>
    <Button
        :disabled="props.disabled || !isSupported || isCapturing"
        :icon="isCapturing ? 'far fa-spinner-third' : 'far fa-camera'"
        class="w-full md:w-auto"
        type="button"
        @click="handleClick"
    >
        {{ isCapturing ? texts.supportForm.fileUpload.capturingButton : texts.supportForm.fileUpload.captureButton }}
    </Button>
    <div
        v-if="localError"
        class="text-sm text-red-600"
    >
        {{ localError }}
    </div>
    <div
        v-else-if="!isSupported"
        class="text-sm text-gray-500"
    >
        {{ texts.supportForm.fileUpload.notSupported }}
    </div>
</template>
